import {
    ReactElement,
    useRef,
    useState,
    ChangeEventHandler,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useEffect,
    useImperativeHandle
} from 'react';
import { CompanyUser } from '@/api';
import {
    useUpdateCompanyUserAction,
    useUploadCompanyUserImageAction
} from '../actions';
import {
    UIToast,
    UIAvatar,
    UIButton,
    UIFormInput,
    UIFormInputRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type ProfileSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Explains why the section is read-only when `canEdit` is false. */
    editDisabledReason?: string;
};

export function MemberProfileSettingsSection(
    props: ProfileSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;

    const { t } = useCompaniesTranslation();

    const formRef = useRef<MemberProfileSettingsSectionFormRef>(null);
    const [isEditing, setIsEditing] = useState(false);
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_profile_settings_section.toast_updated'
        )
    });
    const uploadImage = useUploadCompanyUserImageAction();

    function onSubmit(data: { firstName: string; lastName: string }) {
        const { firstName, lastName } = data;
        updateCompanyUser
            .updateCompanyUser(companyUser.id, { firstName, lastName })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    function onUploadImage(file: File) {
        uploadImage
            .uploadImage(companyUser.id, file)
            .then(() => {
                UIToast.success(
                    t(
                        'components.member_profile_settings_section.toast_image_updated'
                    )
                );
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <MemberProfileSettingsSectionForm
            ref={formRef}
            canEdit={canEdit}
            editDisabledReason={editDisabledReason}
            initialFirstName={companyUser.firstName ?? ''}
            initialLastName={companyUser.lastName ?? ''}
            initialImage={companyUser.image}
            loading={updateCompanyUser.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            imageLoading={uploadImage.loading}
            onSubmit={onSubmit}
            onUploadImage={onUploadImage}
        />
    );
}

type ProfileSettingsSectionFormProps = {
    canEdit: boolean;
    /** Explains why the section is read-only when `canEdit` is false. */
    editDisabledReason?: string;
    initialFirstName: string;
    initialLastName: string;
    initialImage: string;
    imageLoading: boolean;
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: { firstName: string; lastName: string }) => void;
    onUploadImage: (file: File) => void;
};

type MemberProfileSettingsSectionFormRef = {
    resetForm: () => void;
};

const MemberProfileSettingsSectionForm = forwardRef<
    MemberProfileSettingsSectionFormRef,
    ProfileSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        editDisabledReason,
        initialFirstName,
        initialLastName,
        initialImage,
        loading,
        isEditing,
        setIsEditing,
        onSubmit,
        onUploadImage
    } = props;

    const { t } = useCompaniesTranslation();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFileInputClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                onUploadImage(file);
            }
        }
    };

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * First name
     */
    const firstNameRef = useRef<UIFormInputRef>(null);
    const [firstName, setFirstName] = useState(initialFirstName);
    const [firstNameError, setFirstNameError] = useState('');
    const checkFirstName = useCallback(
        (firstName: string): string => {
            const trimmed = firstName.trim();
            if (trimmed.length === 0) {
                return t(
                    'components.member_profile_settings_section.first_name_error_required'
                );
            } else if (trimmed.length > 100) {
                return t(
                    'components.member_profile_settings_section.first_name_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setFirstNameError(checkFirstName(firstName));
        }
    }, [firstName, showRealTimeErrors, checkFirstName]);

    /**
     * Last name
     */
    const lastNameRef = useRef<UIFormInputRef>(null);
    const [lastName, setLastName] = useState(initialLastName);
    const [lastNameError, setLastNameError] = useState('');
    const checkLastName = useCallback(
        (lastName: string): string => {
            const trimmed = lastName.trim();
            if (trimmed.length === 0) {
                return t(
                    'components.member_profile_settings_section.last_name_error_required'
                );
            } else if (trimmed.length > 100) {
                return t(
                    'components.member_profile_settings_section.last_name_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setLastNameError(checkLastName(lastName));
        }
    }, [lastName, showRealTimeErrors, checkLastName]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkFirstName(firstName) && !checkLastName(lastName)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ firstName, lastName });
        }
    };

    function resetForm() {
        setFirstName(initialFirstName);
        setFirstNameError('');
        setLastName(initialLastName);
        setLastNameError('');
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    if (!isEditing) {
        return (
            <NonEditableForm
                firstName={initialFirstName}
                lastName={initialLastName}
                image={initialImage}
                canEdit={canEdit}
                editDisabledReason={editDisabledReason}
                onEditClick={() => setIsEditing(true)}
            />
        );
    }

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t(
                        'components.member_profile_settings_section.title'
                    )}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    gap: 16,
                                    alignItems: 'center'
                                }}
                            >
                                <UIAvatar size={64} img={initialImage} alt="" />
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4
                                    }}
                                >
                                    <UIText>
                                        {t(
                                            'components.member_profile_settings_section.image_label'
                                        )}
                                    </UIText>
                                    <UIText type="default2">
                                        {t(
                                            'components.member_profile_settings_section.image_description'
                                        )}
                                    </UIText>
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIButton
                                    text={t('shared.labels.edit')}
                                    type="button"
                                    onClick={handleFileInputClick}
                                    loading={props.imageLoading}
                                    disabled={props.imageLoading}
                                />
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept=".jpg,.jpeg,.png"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_profile_settings_section.first_name_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_profile_settings_section.first_name_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={firstNameRef}
                                    name="First Name"
                                    value={firstName}
                                    placeholder={t(
                                        'components.member_profile_settings_section.first_name_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={firstNameError}
                                    required
                                    maxLength={100}
                                    onChange={setFirstName}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_profile_settings_section.last_name_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_profile_settings_section.last_name_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={lastNameRef}
                                    name="Last Name"
                                    value={lastName}
                                    placeholder={t(
                                        'components.member_profile_settings_section.last_name_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={lastNameError}
                                    required
                                    maxLength={100}
                                    onChange={setLastName}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                <UISectionFooter
                    actions={[
                        {
                            variant: 'primary',
                            type: 'submit',
                            loading: loading,
                            disabled: loading,
                            text: loading
                                ? t('shared.labels.saving')
                                : t('shared.labels.save')
                        },
                        {
                            text: t('shared.labels.cancel'),
                            type: 'button',
                            disabled: loading,
                            onClick: resetForm
                        }
                    ]}
                />
            </form>
        </UISection>
    );
});

/** What the section shows until Edit is pressed. */
function NonEditableForm(props: {
    firstName: string;
    lastName: string;
    image: string;
    canEdit: boolean;
    editDisabledReason?: string;
    onEditClick: () => void;
}): ReactElement {
    const {
        firstName,
        lastName,
        image,
        canEdit,
        editDisabledReason,
        onEditClick
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <UISection>
            <UISectionHeader
                title={t('components.member_profile_settings_section.title')}
                rightButtons={[
                    {
                        text: t('shared.labels.edit'),
                        variant: 'textDefault',
                        type: 'button',
                        disabled: !canEdit,
                        disabledTooltip: editDisabledReason,
                        onClick: onEditClick
                    }
                ]}
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                gap: 16,
                                alignItems: 'center'
                            }}
                        >
                            <UIAvatar size={64} img={image} alt="" />
                            <UIText>
                                {t(
                                    'components.member_profile_settings_section.image_label'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }} />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText>
                                {t(
                                    'components.member_profile_settings_section.first_name_label'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIFormInput
                                name="First Name"
                                value={firstName}
                                disabled={true}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIText>
                                {t(
                                    'components.member_profile_settings_section.last_name_label'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UIFormInput
                                name="Last Name"
                                value={lastName}
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>
            </UISectionContent>
        </UISection>
    );
}
